import DocumentViewerModal from "components/DocumentViewer";
import FileActions from "components/FileDownloadButtons";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import {
  addLitigation,
  editLitigationPatch,
} from "services/prosecutorDecipilineService";
import { CouncilLitigation, Litigation } from "types";
import { ROLES } from "utils/roles";
import AddLitigationFeedbackModal from "./AddLitigationFeedbackModal";

interface LitigationProps {
  litigationData: Litigation[];
  councilLitigationData: CouncilLitigation[] | [];
  onLitigationRefresh: any;
}

const calculate_remaining_days = ({ date }: { date: string }) => {
  const appeal = new Date(date ?? "");
  const today = new Date();

  // Zero out time to compare dates only
  appeal.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffInMs = appeal.getTime() - today.getTime();

  const remainingDays = Math.floor(diffInMs / msPerDay);

  return remainingDays;
};

export default function LitigationCard({
  litigationData,
  councilLitigationData,
  onLitigationRefresh,
}: LitigationProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const canViewDailyHearing = useHasRole([
    ROLES.INSPECTION_PROSECUTOR,
    ROLES.INSPECTION_DIRECTOR,
    ROLES.INSPECTION_COORDINATOR,
  ]);
  const canAddLitigationFeedback = useHasRole([
    ROLES.INSPECTION_DIRECTOR,
    ROLES.INSPECTION_COORDINATOR,
  ]);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedLitigation, setSelectedLitigation] = useState<any>(null);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const [dateDraft, setDateDraft] = useState({
    hearing_date: "",
    next_appointment_date: "",
  });
  const [isDateUpdating, setIsDateUpdating] = useState(false);

  const toDateInputValue = (value?: string) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const toggleCardExpansion = (index: number) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const getHeaderInfo = (litigation: any) => {
    const hasNextAppointment = !!litigation?.next_appointment_date;
    const hasCouncilDecision = !!litigation?.council_decision;

    if (hasCouncilDecision) {
      return {
        title: "Final Office of Council Decision",
        subtitle: "Official ruling issued",
        status: "Decided",
        variant: "success",
        icon: "⚖️",
        hasDecision: true,
      };
    }

    if (hasNextAppointment) {
      const nextAppointmentDate = new Date(litigation.next_appointment_date);
      const today = new Date();
      const diffTime = nextAppointmentDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      let statusVariant = "info";
      let statusText = "Scheduled";

      if (diffDays < 0) {
        statusVariant = "error";
        statusText = "Overdue";
      } else if (diffDays <= 7) {
        statusVariant = "warning";
        statusText = "Upcoming";
      }

      return {
        title: `Next Appointment Date: ${nextAppointmentDate.toLocaleDateString()}`,
        subtitle: litigation?.hearing_date
          ? `Last Hearing: ${new Date(litigation.hearing_date).toLocaleDateString()}`
          : "",
        status: statusText,
        variant: statusVariant,
        icon: "📅",
        hasDecision: false,
      };
    }

    // Default case
    return {
      title: "Office of Council Decision Submitted by Prosecutor",
      subtitle: "Final verdict issued",
      status: "Decision Given",
      variant: "info",
      icon: "📋",
      hasDecision: false,
    };
  };

  const getStatusBadgeClass = (variant: string) => {
    const baseClasses = "px-3 py-1 rounded-full text-xs font-semibold border";

    switch (variant) {
      case "success":
        return `${baseClasses} bg-emerald-100 text-emerald-800 border-emerald-200`;
      case "error":
        return `${baseClasses} bg-red-100 text-red-800 border-red-200`;
      case "warning":
        return `${baseClasses} bg-amber-100 text-amber-800 border-amber-200`;
      case "info":
      default:
        return `${baseClasses} bg-slate-100 text-slate-800 border-slate-200`;
    }
  };

  const sortedLitigationData = [...(litigationData || [])]?.sort((a, b) => {
    const aHasDecision = !!a.council_decision_summary;
    const bHasDecision = !!b.council_decision_summary;

    if (aHasDecision && !bHasDecision) return -1;
    if (!aHasDecision && bHasDecision) return 1;

    const dateA = new Date(a?.next_appointment_date ?? "").getTime();
    const dateB = new Date(b?.next_appointment_date ?? "").getTime();

    return dateA - dateB;
  });

  const startDateEdit = (item: Litigation) => {
    setEditingDateId(item.id ?? null);
    setDateDraft({
      hearing_date: toDateInputValue(item.hearing_date as string),
      next_appointment_date: toDateInputValue(
        item.next_appointment_date as string,
      ),
    });
  };

  const cancelDateEdit = () => {
    setEditingDateId(null);
    setDateDraft({ hearing_date: "", next_appointment_date: "" });
  };

  const saveDateEdit = async (item: Litigation) => {
    if (!item?.id) {
      showToast("Litigation id not found", "error");
      return;
    }

    const currentHearingDate = toDateInputValue(item.hearing_date as string);
    const currentNextDate = toDateInputValue(
      item.next_appointment_date as string,
    );

    const payload: Record<string, any> = { id: item.id };

    if (
      dateDraft.hearing_date &&
      dateDraft.hearing_date !== currentHearingDate
    ) {
      payload.hearing_date = dateDraft.hearing_date;
    }

    if (
      dateDraft.next_appointment_date &&
      dateDraft.next_appointment_date !== currentNextDate
    ) {
      payload.next_appointment_date = dateDraft.next_appointment_date;
    }

    if (!payload.hearing_date && !payload.next_appointment_date) {
      showToast("No date change detected", "error");
      return;
    }

    try {
      setIsDateUpdating(true);
      await editLitigationPatch(payload, payload.id, false);
      await Promise.resolve(onLitigationRefresh?.());
      cancelDateEdit();
      showToast("Date updated", "success");
    } catch (err) {
      showToast("Failed to update date", "error");
    } finally {
      setIsDateUpdating(false);
    }
  };

  return (
    <div className="mb-8">
      {sortedLitigationData?.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
          No litigation records available yet.
        </div>
      )}

      <div className="space-y-4">
        {sortedLitigationData?.map((obj: any, index: number) =>
          canViewDailyHearing ? (
            (() => {
              const headerInfo = getHeaderInfo(obj);

              return (
                <div
                  key={obj.id}
                  className="bg-white rounded-lg border border-gray-300 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200"
                >
                  {/* Card Header - Always Visible */}
                  <div
                    className={`px-4 py-3 border-b-2 border-gray-200 cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
                      expandedCards.has(index) ? "bg-gray-50" : ""
                    } ${obj?.council_decision_summary ? "border-l-4 border-red-600" : ""}`}
                    onClick={() => toggleCardExpansion(index)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex items-center justify-center w-10 h-10 rounded ${
                            headerInfo.hasDecision ? "bg-red-50" : "bg-gray-50"
                          }`}
                        >
                          <span className="text-xl">{headerInfo.icon}</span>
                        </div>

                        <div>
                          <h3 className="text-base font-semibold text-gray-900">
                            {headerInfo.title}
                          </h3>
                          {headerInfo.subtitle && (
                            <p className="text-xs text-gray-600 mt-0.5">
                              {headerInfo.subtitle}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <span
                          className={getStatusBadgeClass(headerInfo.variant)}
                        >
                          {headerInfo.status}
                        </span>

                        <svg
                          className={`w-4 h-4 text-gray-600 transform transition-transform duration-200 ${
                            expandedCards.has(index) ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Collapsible Content */}
                  {expandedCards.has(index) && (
                    <div className="px-4 py-3">
                      {/* Key Dates - Simple Layout */}
                      {(obj?.hearing_date || obj?.next_appointment_date) && (
                        <div className="mb-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">
                            Key Dates
                          </h4>
                          <div className="space-y-2">
                            {obj?.hearing_date && (
                              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                <span className="text-base text-gray-600">
                                  {t("hearingDate")}
                                </span>
                                <span className="text-base font-medium text-gray-900">
                                  {obj.hearing_date}
                                </span>
                              </div>
                            )}

                            {obj?.next_appointment_date && (
                              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                <span className="text-base text-gray-600">
                                  {t("nextAppointmentDate")}
                                </span>
                                <span className="text-base font-medium text-gray-900">
                                  {obj.next_appointment_date}
                                </span>
                              </div>
                            )}

                            <div className="pt-2">
                              {editingDateId === obj.id ? (
                                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">
                                        {t("hearingDate")}
                                      </label>
                                      <input
                                        type="date"
                                        value={dateDraft.hearing_date}
                                        onChange={(e) =>
                                          setDateDraft((prev) => ({
                                            ...prev,
                                            hearing_date: e.target.value,
                                          }))
                                        }
                                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">
                                        {t("nextAppointmentDate")}
                                      </label>
                                      <input
                                        type="date"
                                        value={dateDraft.next_appointment_date}
                                        onChange={(e) =>
                                          setDateDraft((prev) => ({
                                            ...prev,
                                            next_appointment_date:
                                              e.target.value,
                                          }))
                                        }
                                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      disabled={isDateUpdating}
                                      onClick={() => saveDateEdit(obj)}
                                      className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                                    >
                                      {isDateUpdating
                                        ? "Updating..."
                                        : "Save Date"}
                                    </button>
                                    <button
                                      type="button"
                                      disabled={isDateUpdating}
                                      onClick={cancelDateEdit}
                                      className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => startDateEdit(obj)}
                                  className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                                >
                                  {t("updateDate")}
                                </button>
                              )}
                            </div>

                            {councilLitigationData[0]?.council_decision_day && (
                              <div className="flex justify-between items-center py-2">
                                <span className="text-base text-gray-600">
                                  {t("remainingDaysForAppeal")}
                                </span>
                                <span
                                  className={`text-base font-medium ${
                                    calculate_remaining_days({
                                      date:
                                        councilLitigationData[0]
                                          ?.council_decision_day ?? "",
                                    }) < 0
                                      ? "text-red-600"
                                      : "text-gray-900"
                                  }`}
                                >
                                  {(() => {
                                    const days = calculate_remaining_days({
                                      date:
                                        councilLitigationData[0]
                                          ?.council_decision_day ?? "",
                                    });
                                    const isExpired = days < 0;
                                    const absDays = Math.abs(days);
                                    const dayLabel =
                                      absDays === 1 ? t("day") : t("days");

                                    if (
                                      !councilLitigationData[0]
                                        ?.council_decision_day
                                    )
                                      return "-";

                                    return isExpired
                                      ? `Expired ${absDays} ${dayLabel} ago`
                                      : `${days} ${dayLabel}`;
                                  })()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Simple List of Details */}
                      <div className="space-y-4">
                        {obj?.given_order && (
                          <div className="border-b border-gray-100 pb-3">
                            <p className="text-base font-semibold text-gray-900 mb-1">
                              {t("councilOrder")}
                            </p>
                            <p className="text-base text-gray-700">
                              {obj.given_order}
                            </p>
                          </div>
                        )}

                        {obj?.prosecutor_opinion_on_decision_appeal && (
                          <div className="border-b border-gray-100 pb-3">
                            <p className="text-base font-semibold text-gray-900 mb-1">
                              {t("prosecutorOpinion")}
                            </p>
                            <p className="text-base text-gray-700">
                              {obj.prosecutor_opinion_on_decision_appeal}
                            </p>
                          </div>
                        )}

                        {obj?.daily_report_summary && (
                          <div className="border-b border-gray-100 pb-3">
                            <p className="text-base font-semibold text-gray-900 mb-1">
                              {t("dailyReportSummary")}
                            </p>
                            <p className="text-base text-gray-700">
                              {obj.daily_report_summary}
                            </p>
                          </div>
                        )}

                        {obj?.appointment_reason && (
                          <div className="border-b border-gray-100 pb-3">
                            <p className="text-base font-semibold text-gray-900 mb-1">
                              {t("appointmentReason")}
                            </p>
                            <p className="text-base text-gray-700">
                              {obj.appointment_reason}
                            </p>
                          </div>
                        )}

                        {obj?.prosecutor_opinion_on_decision && (
                          <div className="border-b border-gray-100 pb-3">
                            <p className="text-base font-semibold text-gray-900 mb-1">
                              {t("prosecutorOpinionOnDecision")}
                            </p>
                            <p className="text-base text-gray-700">
                              {obj.prosecutor_opinion_on_decision}
                            </p>
                          </div>
                        )}

                        {obj?.director_feedbacks?.map(
                          (feedback: any, idx: number) => (
                            <div
                              key={idx}
                              className="border-b border-gray-100 pb-3"
                            >
                              <p className="text-base font-semibold text-gray-900 mb-1">
                                {t("feedback")}
                              </p>
                              <p className="text-base text-gray-700">
                                {feedback?.comment || "-"}
                              </p>
                            </div>
                          ),
                        )}

                        {obj?.council_decision_summary && (
                          <div className="border-b border-gray-100 pb-3">
                            <p className="text-base font-semibold text-gray-900 mb-1">
                              {t("councilDecisionSummary")}
                            </p>
                            <p className="text-base text-gray-700">
                              {obj.council_decision_summary}
                            </p>
                          </div>
                        )}

                        {obj?.council_decision_type && (
                          <div className="border-b border-gray-100 pb-3">
                            <p className="text-base font-semibold text-gray-900 mb-1">
                              {t("councilDecisionType")}
                            </p>
                            <p className="text-base text-gray-700">
                              {obj.council_decision_type}
                            </p>
                          </div>
                        )}

                        {obj?.discipline_measure_type && (
                          <div className="border-b border-gray-100 pb-3">
                            <p className="text-base font-semibold text-gray-900 mb-1">
                              {t("decisionMeasureType")}
                            </p>
                            <p className="text-base text-gray-700">
                              {obj.discipline_measure_type}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Documents Section */}
                      {obj?.litigation_verdict_documents &&
                        obj?.litigation_verdict_documents.length > 0 && (
                          <div className="mt-4">
                            <DocumentViewerModal
                              documents={
                                obj?.litigation_verdict_documents as any
                              }
                              baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/litigation-documents/document`}
                              layout="list"
                              label="Letter Upload"
                              hideEmptyFile={true}
                            />
                          </div>
                        )}

                      {/* Files Section - Simple Design */}
                      <div className="space-y-2 mt-4">
                        {obj?.council_decision && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded border border-gray-300">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center">
                                <span className="text-red-600 text-xs font-bold">
                                  PDF
                                </span>
                              </div>
                              <div>
                                <p className="text-base font-medium text-gray-800">
                                  {t("councilDecisionFile")}
                                </p>
                              </div>
                            </div>
                            <FileActions
                              fileUrl={obj?.council_decision || ""}
                            />
                          </div>
                        )}

                        {obj?.termination_document && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded border border-gray-300">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center">
                                <span className="text-red-600 text-xs font-bold">
                                  PDF
                                </span>
                              </div>
                              <div>
                                <p className="text-base font-medium text-gray-800">
                                  {t("terminationFile")}
                                </p>
                              </div>
                            </div>
                            <FileActions
                              fileUrl={obj?.termination_document || ""}
                            />
                          </div>
                        )}
                      </div>

                      {/* Add Feedback Button - Simple Design */}
                      {canAddLitigationFeedback &&
                        obj?.hearing_date &&
                        obj?.director_feedbacks &&
                        obj.director_feedbacks.length <= 0 && (
                          <div className="mt-4 pt-3 border-t border-gray-300">
                            <button
                              className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-2 px-4 rounded transition-all duration-200 flex items-center justify-center gap-2"
                              onClick={() => {
                                setSelectedLitigation(obj);
                                setIsFeedbackOpen(true);
                              }}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 4v16m8-8H4"
                                />
                              </svg>
                              <span className="text-base">
                                {t("addFeedback")}
                              </span>
                            </button>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <></>
          ),
        )}
      </div>

      {litigationData && (
        <AddLitigationFeedbackModal
          isOpen={isFeedbackOpen}
          setIsOpen={setIsFeedbackOpen}
          litigationData={selectedLitigation}
          onRefresh={onLitigationRefresh}
        />
      )}
    </div>
  );
}
